import { engineComponent } from "neo-common";
import { BaseController } from "./BaseController";
import { Symbols } from "../Symbols";
import { WinAnimator } from "../WinAnimator";
import { Machine } from "../Machine";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { GameManager } from "../../Managers/GameManager";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../../Utils/FrameTimer";
import { PlayList } from "../../Components/Sounds/PlayList";
import { Tweens } from "../../Components//Effects/Tweens";
import { ShapeElement } from "../../Components/Elements/Graphics/ShapeElement";
import { StageController } from "../../Components/StageController";


import { ModuleManager } from "../../Managers/ModuleManager";
import { DataManager } from "../../Managers/DataManager";
import { CustomUtils } from "../../Utils/CustomUtils";
import { AmbientBackground } from "../../Utils/AmbientBackground";
import { WinAnimatorExtended } from "../../CustomComponents/WinAnimatorExtended";
import { MachineExtended } from "../../CustomComponents/MachineExtended";
import { PaylinesExtended } from "../../CustomComponents/PaylinesExtended";
import { LayoutManager } from "../../Managers/LayoutManager";
import { LayoutContainerElement } from "../../CustomComponents/LayoutContainerElement";
import { GroupTweens } from "../../CustomComponents/GroupTweens";
import { TimelineElement } from "../../Components/Elements/Graphics/TimelineElement";

@engineComponent(ComponentTypes.DisplayObject)
export class BaseView extends LayoutContainerElement {
    public static LayoutManager: LayoutManager;

    static FsMessageBox: TimelineElement;

    private _wasFinished: boolean = false;
    private _controller: BaseController;
    private _introDelay: FrameTimerEventEmitter;
    private _outroDelay: FrameTimerEventEmitter;
    private _machineStoppedDelay: FrameTimerEventEmitter;
    private _paylines: PaylinesExtended;
    private _symbols: Symbols;
    private _machine: MachineExtended;
    private _winAnimator: WinAnimatorExtended;
    private _groupTweens: SwitchElement;

    constructor() {
        super();
        this._introDelay = CreateFrameTimer("introDelay");
        this._introDelay.on(FrameTimerEvents.Complete, this.introFinished.bind(this));
        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));
        this._machineStoppedDelay = CreateFrameTimer("machineStoppedDelay");
        this._machineStoppedDelay.on(FrameTimerEvents.Complete, this.machineStoppedDelay.bind(this));
    }

    public create(node: Element): void {
        super.create(node);

        BaseView.LayoutManager = new LayoutManager();

        if (this.childs["Symbols"] != null) {
            this._symbols = this.childs["Symbols"];
            this.removeChild(this._symbols);
        }
        if (this.childs["Paylines"] != null) this._paylines = this.childs["Paylines"];
        if (this.childs["Machine"] != null) this._machine = this.childs["Machine"];
        if (this.childs["WinAnimator"] != null) this._winAnimator = this.childs["WinAnimator"];
        if (this.childs["FsMessageBox"] != null) BaseView.FsMessageBox = this.childs["FsMessageBox"];
        if (this.childs["GroupTweens"] != null) this._groupTweens = this.childs["GroupTweens"];

        if (BaseView.FsMessageBox) {
            BaseView.FsMessageBox.visible = false;
        }

        // set childs to GroupTweens object
        if (this._groupTweens) {
            let groupTweenElements: Object = this._groupTweens.getChildElements();
            if (groupTweenElements) {
                for (let key in groupTweenElements) {
                    if (groupTweenElements.hasOwnProperty(key)) {
                        if (groupTweenElements[key] instanceof GroupTweens) {
                            (<GroupTweens>groupTweenElements[key]).setChilds(this.childs);
                        }
                    }
                }
            }
        }
    }

    public initReceived(controller: BaseController): void {
        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.baseResponse != null) {
                    if (GameManager.baseResponse.initData != null && GameManager.baseResponse.initData.reels != null) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(MachineExtended.RollFinished, this.rollFinished.bind(this));
                        this._machine.on(MachineExtended.OneReelRollFinished, this.oneReelRollFinished.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStopped.bind(this));
                        this._machine.initialize(
                            this._symbols,
                            GameManager.baseResponse.initData.reels,
                            this._winAnimator,
                            this.childs["Teasing"],
                            this.childs["NoTrimWinContainer"],
                        );
                    }

                    if (
                        GameManager.baseResponse.screen != null &&
                        GameManager.baseResponse.screen.reels != null &&
                        GameManager.baseResponse.screen.reels.length !== 0
                    ) {
                        if (this._winAnimator != null) {
                            this._winAnimator.on(
                                WinAnimator.FinishWinCalculation,
                                this.finishWinCalculation.bind(this),
                            );
                            this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                            this._winAnimator.initialize(
                                this._symbols,
                                this._paylines,
                                this.childs["PaylinesLines"],
                                this.childs["NoWinContainer"],
                                this.childs["WinContainer"],
                                this,
                            );
                            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
                        }
                        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reels in response");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no base reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }

        this.visible = false;
        if (ModuleManager.gambleActive()) {
            this.visible = true;

            if (this._groupTweens) {
                this._groupTweens.goToCase(GroupTweens.CaseGambleInit);
            }
        }
    }

    public spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    public spin(): void {
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.resetVolume("SND_BaseLoop");
        PlayList.loop("SND_BaseLoop");

        AmbientBackground.changeAmbientBackground("base", 30);
        this._machine.spinStart(this._winAnimator.reset(true, true), true, this._winAnimator.currentSymbols);
    }

    public stop(): void {
        this._machine.spinStop(null, true);
    }

    public collect(): void {
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.resetVolume("SND_BaseLoop");
        PlayList.loop("SND_BaseLoop");

        AmbientBackground.changeAmbientBackground("base", 30);
        this._winAnimator.reset(true);
    }

    public initIntro(): void {
        // this.initBaseLoop();

        if (GameManager.gameResponse.totalWin > 0) {
            PlayList.loop("SND_BaseRecapLoop");
        } else {
            PlayList.resetVolume("SND_BaseLoop");
            PlayList.loop("SND_BaseLoop");
        }

        this.visible = true;

        if (this._groupTweens) {
            this._groupTweens.goToCase(GroupTweens.CaseInitIntro);
        }

        this._winAnimator.showInitWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin);
    }

    public intro(long: boolean = false): void {
        // this.initBaseLoop();

        this.visible = true;

        if (GameManager.gameResponse.totalWin > 0) {
            // PlayList.setVolume('SND_BaseLoop', 0);
            PlayList.stop("SND_BaseLoop");
            PlayList.loop("SND_BaseRecapLoop");
        } else {
            PlayList.stop("SND_BaseRecapLoop");
            PlayList.resetVolume("SND_BaseLoop");
            PlayList.loop("SND_BaseLoop");

            this.resetWinAnimator();
        }

        if (long) {
            if (this._groupTweens) {
                this._groupTweens.goToCase(GroupTweens.CaseFreespinsToBase);
            }

            this._introDelay.start(60);
        } else {
            if (this._groupTweens) {
                this._groupTweens.goToCase(GroupTweens.CaseGambleToBase);
            }
            this._introDelay.start(30);
        }
    }

    private introFinished(): void {
        this._controller.introFinished();
    }

    public outro(): void {
        PlayList.setVolume("SND_BaseLoop", 0);
        PlayList.stop("SND_BaseRecapLoop");

        this.visible = true;

        let wasFinished: boolean = this._wasFinished;
        let inHighWins: boolean = this._winAnimator.reset(true);

        if (ModuleManager.freespinsActive()) {
            AmbientBackground.changeAmbientBackground("fs", 120);

            PlayList.stop("SND_FSWait");
            PlayList.play("SND_FSTransIn");

            if (BaseView.FsMessageBox) {
                BaseView.FsMessageBox.visible = true;
                BaseView.FsMessageBox.goToOutro();
            }

            if (this._groupTweens) {
                this._groupTweens.goToCase(GroupTweens.CaseBaseToFreespins);
            }

            this._outroDelay.start(70);
        } else {
            PlayList.play("SND_GambleTransition");
            if (wasFinished) {
                this._outroDelay.start(30);
                if (this._groupTweens) {
                    this._groupTweens.goToCase(GroupTweens.CaseBaseToGamble);
                }
            } else {
                if (inHighWins) {
                    this._outroDelay.start(90);
                    if (this._groupTweens) {
                        this._groupTweens.goToCase(GroupTweens.CaseBaseToGambleHighWins);
                    }
                } else {
                    this._outroDelay.start(60);
                    if (this._groupTweens) {
                        this._groupTweens.goToCase(GroupTweens.CaseBaseToGambleWins);
                    }
                }
            }
        }
    }

    private outroFinished(): void {
        if (BaseView.FsMessageBox) {
            BaseView.FsMessageBox.visible = false;
        }

        this._controller.outroFinished();
        this._winAnimator.resetTimer();

        if (ModuleManager.freespinsActive()) {
            let inHighWins: boolean = this._winAnimator.reset(true);
            this.visible = false;
        }
    }

    public disableLogo(): void {
        if (this.childs["Logo"] != null) {
            this.removeChild(this.childs["Logo"]);
        }
    }

    public resetWinAnimator(): void {
        this._winAnimator.reset(true);
    }

    public disableBase(): void {
        this._winAnimator.resetTimer();
    }

    resetScreen(): void {
        if (this._winAnimator) {
            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
        }

        if (this._machine) {
            this._machine.spinReset();
            this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        }

        const node = document.createElement("ResetOverlay");
        node.setAttribute("width", StageController.Width.toString());
        node.setAttribute("height", StageController.Height.toString());
        node.setAttribute("color", "000000");
        const resetOverlay = new ShapeElement();
        resetOverlay.create(node);

        this.addChild(resetOverlay);
        Tweens.removeTweens(resetOverlay, "alpha");
        Tweens.alphaTween(resetOverlay, {
            alphas: [1, 0],
            tweenDuration: 10,
            callbackFunction: () => {
                resetOverlay.destroy();
            },
        });

        PIXI.sound.unmuteAll();
        PlayList.stopAll();

        this.initIntro();
    }

    public selectPayline(): void {}

    public toggleSounds(enable: boolean): void {
        if (enable) {
            PlayList.resetVolume("SND_BaseLoop");
            PlayList.loop("SND_BaseLoop");
        } else {
            PlayList.setVolume("SND_BaseLoop", 0);
            PlayList.stop("SND_BaseRecapLoop");
        }
    }

    private initBaseLoop(): void {
        PlayList.resetVolume("SND_BaseLoop");
        PlayList.loop("SND_BaseLoop");
    }

    private oneReelRollFinished(reelIndex: number): void {
        this._winAnimator.onOneReelRollFinished(reelIndex);
    }

    private rollFinished(): void {
        this._controller.rollFinished();
    }

    private onReelStopped(): void {
        this._winAnimator.onReelStopped(GameManager.baseResponse.screen.reels, this._machine.actualReelStopped);
    }

    private machineStopped(): void {
        this._wasFinished = false;
        this._controller.machineStopped();

        PlayList.resetVolume("SND_BaseLoop");

        // if(ModuleManager.freespinsEnabled()) {
        //     this._machineStoppedDelay.start(30);
        // } else {
        this.machineStoppedDelay();
        // }
    }

    machineStoppedDelay(): void {
        this._machineStoppedDelay.stop();

        if (this._winAnimator.showWins(GameManager.baseResponse.wins, CustomUtils.getBaseCurrentWin(true))) {
            PlayList.setVolume("SND_BaseLoop", 0);
        }
    }

    private finishWinCalculation(): void {
        this._wasFinished = true;
        this._controller.winCalculationFinished();
    }

    private finishWinAnimator(afterBonusGame: boolean = false): void {
        if (!DataManager.Autoplay || ModuleManager.freespinsEnabled()) {
            PlayList.setVolume("SND_BaseLoop", 0);
            if (ModuleManager.freespinsEnabled()) {
                PlayList.loop("SND_FSWait");
            }
        }

        this._controller.allWinsFinished();
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Base/BaseView.ts
 **/